/**
 * ProgressBar — accessible determinate / indeterminate progress indicator.
 *
 * Mirrors the ARIA progressbar pattern. Determinate bars surface their
 * current value through `aria-valuenow` so screen readers track
 * progression; indeterminate bars omit `aria-valuenow` and render a
 * looping shimmer so the consumer can signal "work is in flight"
 * without committing to a known total.
 *
 * Single responsibility: render the progress track and fill. The
 * caller owns the value, the formatting of any companion label, and
 * the indeterminate-vs-determinate decision.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { GRAY, PRIMARY, SAFE, WARN } from "../design-system/tokens.js";
import { cn } from "../styling/index.js";

export type ProgressBarTone = "neutral" | "info" | "success" | "warning";

export interface ProgressBarProps {
  /** Current value, in the same units as `max`. Ignored when `indeterminate`. */
  readonly value?: number;
  /** Maximum value. Defaults to 100. */
  readonly max?: number;
  /**
   * When true, the bar renders without a value and announces an
   * indeterminate state to assistive tech.
   */
  readonly indeterminate?: boolean;
  /** Visual tone — `info` (blue), `success` (green), `warning` (red), `neutral`. */
  readonly tone?: ProgressBarTone;
  /** Pixel height of the bar. Defaults to 8. */
  readonly height?: number;
  /** Accessible label describing what is in progress. */
  readonly ariaLabel: string;
  /** Optional className passed to the outer wrapper. */
  readonly className?: string;
}

const TONE_FILL: Readonly<Record<ProgressBarTone, string>> = {
  neutral: GRAY[600],
  info: PRIMARY[600],
  success: SAFE[500],
  warning: WARN[500],
};

function clampPercent(value: number, max: number): number {
  if (max <= 0) return 0;
  return Math.max(0, Math.min(100, (value / max) * 100));
}

/** Renders an accessible progress bar. */
export const ProgressBar = React.memo(function ProgressBar({
  value = 0,
  max = 100,
  indeterminate = false,
  tone = "info",
  height = 8,
  ariaLabel,
  className,
}: ProgressBarProps): React.JSX.Element {
  const fill = TONE_FILL[tone];
  const percent = clampPercent(value, max);
  const ariaProps = indeterminate
    ? { "aria-valuemin": 0, "aria-valuemax": max }
    : { "aria-valuemin": 0, "aria-valuemax": max, "aria-valuenow": value };

  return (
    <div
      role="progressbar"
      aria-label={ariaLabel}
      {...ariaProps}
      data-csk-progress-state={indeterminate ? "indeterminate" : "determinate"}
      className={cn("relative w-full overflow-hidden rounded-full", className)}
      style={{ backgroundColor: GRAY[300], height }}
    >
      {indeterminate ? (
        <div
          aria-hidden="true"
          data-csk-progress-shimmer
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "-40%",
            width: "40%",
            backgroundColor: fill,
            borderRadius: 9999,
            animation: "csk-progress-shimmer 1.4s ease-in-out infinite",
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          data-csk-progress-fill
          style={{
            height: "100%",
            width: `${percent}%`,
            backgroundColor: fill,
            borderRadius: 9999,
            transition: "width 200ms ease-out",
          }}
        />
      )}
      <style>{`
        @keyframes csk-progress-shimmer {
          0% { left: -40%; }
          100% { left: 100%; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-csk-progress-shimmer] { animation: none; left: 0; width: 100%; opacity: 0.6; }
        }
      `}</style>
    </div>
  );
});
